import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { PageTitle } from '../components/page-title';
import { ActivityTab } from './components/activity-tab';
import { AuditTab } from './components/audit-tab';
import { CreditTab } from './components/credit-tab';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { SEAT_BASED_ROLE_NAME } from '../../../../backend/src/user/roles/user-roles-defaults';
import { useFetchOrganisations } from '../../api/queries/analyticsQueries/useFetchOrganisations';
import { useUserContext } from '../../Contexts/User';
import { Breadcrumb } from '../components/breadcrumb';
import { Icon } from '../components/icon';
import { Tabs, TabsList, TabsTrigger } from '../components/shadcn/tabs';
import { useHasPermission } from '@/hooks/useHasPermission';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { SearchableCombobox } from '@/new/components/searchable-combobox';

const TABS = ['activity', 'audit', 'credit'] as const;

export function Usage() {
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string>('');

  const { t } = useTranslation();
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const location = useLocation();
  if (!user) return null;

  const activeTab = TABS.find((tabId) => location.pathname.includes(`/usage/${tabId}`)) ?? 'activity';
  const canViewPlatformUsage = hasPermission(Permission.PlatformViewUsage);
  const canViewAudit = hasPermission(Permission.AuditView);
  const isPersonalUsage = user.role?.name === SEAT_BASED_ROLE_NAME;
  const organisationId =
    activeTab !== 'audit' && selectedOrganisationId !== '' ? selectedOrganisationId : user.organisationId;
  const isViewingOtherOrg =
    activeTab !== 'audit' &&
    canViewPlatformUsage &&
    selectedOrganisationId !== '' &&
    selectedOrganisationId !== user.organisationId;

  // Fetch options for operators allowed to view usage across organisations.
  const { data: organisationsData } = useFetchOrganisations(canViewPlatformUsage);
  const organisationOptions =
    (organisationsData &&
      organisationsData.length &&
      organisationsData
        .map((organisation) => ({
          label: organisation.name,
          value: organisation.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))) ||
    [];

  return (
    <>
      <Breadcrumb items={[{ href: '/usage', label: 'Usage', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle
            text="Usage"
            subtext={
              isPersonalUsage
                ? 'View your activity and credit usage.'
                : `Activity and credit usage across your ${t('organisation')}.`
            }
          />
        </PageHeader>

        <Tabs value={activeTab}>
          <div className="flex items-center justify-between gap-4">
            <TabsList className="justify-start">
              <TabsTrigger asChild value="activity">
                <Link to="/usage/activity">
                  <Icon className="lucide--activity" />
                  Activity
                </Link>
              </TabsTrigger>
              {canViewAudit && (
                <TabsTrigger asChild value="audit">
                  <Link to="/usage/audit">
                    <Icon className="lucide--clock" />
                    Audit
                  </Link>
                </TabsTrigger>
              )}
              <TabsTrigger asChild value="credit">
                <Link to="/usage/credit">
                  <Icon className="lucide--credit-card" />
                  Credits
                </Link>
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-4">
              {canViewPlatformUsage && activeTab !== 'audit' && (
                <SearchableCombobox
                  icon="lucide--building"
                  options={organisationOptions || []}
                  value={selectedOrganisationId}
                  onChange={setSelectedOrganisationId}
                  placeholder="Select an organisation"
                  searchPlaceholder="Search organisations..."
                  emptyText="No organisations found"
                  triggerClassName="w-56 justify-between font-normal"
                />
              )}
            </div>
          </div>
        </Tabs>

        <Routes>
          <Route index element={<Navigate to="activity" replace />} />
          <Route
            path="activity"
            element={
              <ActivityTab
                organisationId={organisationId}
                isViewingOtherOrg={isViewingOtherOrg}
                isPersonalUsage={isPersonalUsage}
              />
            }
          />
          <Route
            path="audit"
            element={
              canViewAudit ? (
                <AuditTab organisationId={organisationId} isViewingOtherOrg={isViewingOtherOrg} />
              ) : (
                <Navigate to="../activity" replace />
              )
            }
          />
          <Route
            path="credit"
            element={<CreditTab organisationId={organisationId} isViewingOtherOrg={isViewingOtherOrg} />}
          />
        </Routes>
      </PageContainer>
    </>
  );
}
